import { Dock, PageScroll } from "@Components/Page";
import { gatewayUrl } from "@Constants/gatewayUrl";
import { useUserContext } from "@Hooks/contexts/useUserContext";
import { useTempDocumentPath } from "@Hooks/useTempDocumentPath";
import { styled } from "@mui/material/styles";
import { loadFontRegistry, resolveFontFamily } from "@Styles/fontRegistry";
import { useFontLoader } from "@Styles/useFontLoader";
import { Banner, Button, CheckIcon, EmptyState, ErrorIcon, FormatIcon, GroupedList, Meta, Popover, SearchField, SelectTrigger, Spinner, focusRing } from "@UI";
import { createConfig } from "@Utils/createConfig";
import axios from "axios";
import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
const SEARCH_THRESHOLD = 8;
const formatBorder = (borderDefinition)=>{
    if (borderDefinition === null || borderDefinition === undefined) return undefined;
    const { colorHex, style, widthInPt } = borderDefinition;
    return `${style} ${widthInPt}pt ${colorHex}`;
};
const formatPadding = (borderDefinition)=>borderDefinition === null || borderDefinition === undefined ? undefined : "2pt";
const renderWithNumbering = (style)=>style.numbering ? `${style.numbering.example} ${style.styleName}` : style.styleName;
const withFallback = (family)=>family ? `"${family}", sans-serif` : "inherit";
function previewStyle(style, fontRegistry) {
    return {
        display: "inline-block",
        maxWidth: "100%",
        fontSize: style.fontSizePt ? `${style.fontSizePt}pt` : "8pt",
        fontWeight: style.bold ? "bold" : "normal",
        fontStyle: style.italic ? "italic" : "normal",
        fontFamily: withFallback(resolveFontFamily(style.fontName, fontRegistry)),
        ...style.fontColorHex && {
            "--preview-ink": style.fontColorHex
        },
        borderTop: formatBorder(style.borders?.top),
        paddingTop: formatPadding(style.borders?.top),
        borderRight: formatBorder(style.borders?.right),
        paddingRight: formatPadding(style.borders?.right),
        borderBottom: formatBorder(style.borders?.bottom),
        paddingBottom: formatPadding(style.borders?.bottom),
        borderLeft: formatBorder(style.borders?.left),
        paddingLeft: formatPadding(style.borders?.left)
    };
}
const Preview = styled("span")({
    color: "var(--preview-ink, inherit)",
    "html[data-theme=dark] &": {
        color: "color-mix(in oklab, var(--preview-ink, var(--j-ink)) 30%, var(--j-ink))"
    }
});
const MappingList = styled(GroupedList)({
    margin: 0
});
const MappingRow = styled("div")({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-2)",
    padding: "var(--j-sp-3) 0",
    minWidth: 0,
    "&:first-of-type": {
        paddingTop: 0
    },
    "& + &": {
        borderTop: "1px solid var(--j-line-soft)"
    },
    "& > .preview": {
        overflow: "hidden",
        overflowWrap: "anywhere"
    }
});
const Option = styled("button")({
    ...focusRing,
    width: "100%",
    minHeight: "var(--j-btn-h)",
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)",
    padding: "var(--j-sp-1) var(--j-sp-2)",
    border: 0,
    borderRadius: "var(--j-r-sm)",
    background: "transparent",
    color: "var(--j-ink)",
    textAlign: "left",
    cursor: "pointer",
    "&:focus-visible": {
        outlineOffset: "var(--j-focus-inset-offset)"
    },
    "&:hover": {
        background: "var(--j-hover)"
    },
    "&[aria-selected=true]": {
        background: "var(--j-accent-soft)",
        color: "var(--j-accent-ink)"
    },
    "& > .option-label": {
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        overflowWrap: "anywhere"
    },
    "& > svg": {
        color: "var(--j-accent-ink)"
    }
});
const PopoverSearch = styled(SearchField)({
    marginBottom: "var(--j-sp-1)"
});
const ErrorMeta = styled(Meta)({
    textAlign: "center",
    color: "var(--j-err)"
});
function MappingSelect({ labelId, options, value, onChange, fontRegistry }) {
    const { t } = useTranslation();
    const triggerId = useId();
    const [anchor, setAnchor] = useState(null);
    const [query, setQuery] = useState("");
    const close = ()=>{
        setAnchor(null);
        setQuery("");
    };
    const visible = query ? options.filter((option)=>renderWithNumbering(option).toLowerCase().includes(query.toLowerCase())) : options;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(SelectTrigger, {
        id: triggerId,
        "aria-haspopup": "dialog",
        "aria-labelledby": `${labelId} ${triggerId}`,
        placeholder: t("workflows.StyleConverter.choose_font"),
        error: !value,
        "aria-expanded": !!anchor,
        style: {
            height: "auto",
            minHeight: "var(--j-btn-h-lg)",
            paddingTop: "var(--j-sp-1)",
            paddingBottom: "var(--j-sp-1)"
        },
        onClick: (event)=>setAnchor(anchor ? null : event.currentTarget)
    }, value && /*#__PURE__*/ React.createElement(Preview, {
        style: previewStyle(value, fontRegistry)
    }, renderWithNumbering(value))), /*#__PURE__*/ React.createElement(Popover, {
        open: !!anchor,
        anchorEl: anchor,
        onClose: close,
        role: "dialog",
        placement: "bottom-start",
        width: anchor?.offsetWidth ?? 248,
        "aria-label": t("workflows.StyleConverter.choose_font")
    }, options.length > SEARCH_THRESHOLD && /*#__PURE__*/ React.createElement(PopoverSearch, {
        value: query,
        onChange: setQuery,
        placeholder: t("generic.search")
    }), /*#__PURE__*/ React.createElement("div", {
        role: "listbox",
        "aria-label": t("workflows.StyleConverter.choose_font")
    }, visible.map((option)=>{
        const isSelected = option.styleId === value?.styleId;
        return /*#__PURE__*/ React.createElement(Option, {
            key: option.styleId,
            type: "button",
            role: "option",
            "aria-selected": isSelected,
            tabIndex: -1,
            onClick: ()=>{
                onChange(option.styleId);
                close();
            }
        }, /*#__PURE__*/ React.createElement("span", {
            className: "option-label"
        }, /*#__PURE__*/ React.createElement(Preview, {
            style: previewStyle(option, fontRegistry)
        }, renderWithNumbering(option))), isSelected && /*#__PURE__*/ React.createElement(CheckIcon, {
            size: 14
        }));
    }))));
}
export function StyleMappings({ header, selectedGroupId, onFormatDocument }) {
    const { t } = useTranslation();
    const [isLoadingFonts, setisLoadingFonts] = React.useState(true);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [hasSaveError, setHasSaveError] = useState(false);
    const previewIdPrefix = useId();
    const [currentDocumentFonts, setCurrentDocumentFonts] = useState([]);
    const [referenceDocumentFonts, setReferenceDocumentFonts] = useState([]);
    const [currentToReferenceDocumentMappings, setCurrentToReferenceDocumentMappings] = useState({});
    const [fontRegistry, setFontRegistry] = useState({
        families: []
    });
    const { setFontLinks } = useFontLoader();
    const token = useUserContext((s)=>s?.user?.meta?.access_token.token);
    const getTempDocumentPath = useTempDocumentPath();
    async function fetchConfig(styleGroupId) {
        setisLoadingFonts(true);
        setHasLoadError(false);
        try {
            const header = createConfig(token);
            const path = await getTempDocumentPath();
            const body = {
                path
            };
            const url = `${gatewayUrl}/plugins/word/documentStyles/styleGroup/${styleGroupId}`;
            const { data: { referenceDocumentFonts, uploadedDocumentFonts, styleMappings, cssFontLinks } } = await axios.post(url, body, {
                ...header,
                withCredentials: true
            });
            await setFontLinks(cssFontLinks);
            try {
                setFontRegistry(await loadFontRegistry(token));
            } catch (fontError) {
                console.error("Error loading font registry:", fontError);
            }
            setReferenceDocumentFonts(referenceDocumentFonts);
            setCurrentDocumentFonts(uploadedDocumentFonts);
            setCurrentToReferenceDocumentMappings(styleMappings);
        } catch (err) {
            console.error("Error fetching mapping config:", err);
            setHasLoadError(true);
        } finally{
            setisLoadingFonts(false);
        }
    }
    useEffect(()=>{
        if (selectedGroupId) {
            fetchConfig(selectedGroupId);
        }
    }, [
        selectedGroupId
    ]);
    const upsertMapping = async (referenceStyleId, documentStyleId)=>{
        if (!selectedGroupId) return false;
        try {
            setHasSaveError(false);
            const url = `${gatewayUrl}/plugins/word/documentStyles/styleGroup/${selectedGroupId}/upsertMapping`;
            const header = createConfig(token);
            await axios.put(url, {
                documentStyleId,
                referenceStyleId
            }, header);
            return true;
        } catch (err) {
            console.error("Failed to upsert mapping:", err);
            setHasSaveError(true);
            return false;
        }
    };
    const handleMappingChange = async (referenceStyleId, documentStyleId)=>{
        const previous = currentToReferenceDocumentMappings[documentStyleId];
        setCurrentToReferenceDocumentMappings((prev)=>({
                ...prev,
                [documentStyleId]: referenceStyleId
            }));
        if (await upsertMapping(referenceStyleId, documentStyleId)) return;
        setCurrentToReferenceDocumentMappings((prev)=>prev[documentStyleId] === referenceStyleId ? {
                ...prev,
                [documentStyleId]: previous
            } : prev);
    };
    const unmappedCount = currentDocumentFonts.filter((documentFont)=>!(currentToReferenceDocumentMappings[documentFont.styleId] ?? "").length).length;
    const areAllMapped = currentDocumentFonts.length > 0 && unmappedCount === 0;
    if (isLoadingFonts) {
        return /*#__PURE__*/ React.createElement(PageScroll, null, header, /*#__PURE__*/ React.createElement(EmptyState, {
            icon: /*#__PURE__*/ React.createElement(Spinner, {
                size: "lg"
            }),
            tone: "neutral",
            text: t("workflows.StyleConverter.extracting_fonts_from_your_document")
        }));
    }
    if (hasLoadError) {
        return /*#__PURE__*/ React.createElement(PageScroll, null, header, /*#__PURE__*/ React.createElement(EmptyState, {
            icon: /*#__PURE__*/ React.createElement(ErrorIcon, null),
            tone: "neutral",
            text: t("workflows.StyleConverter.loadFontsFailed"),
            actions: /*#__PURE__*/ React.createElement(Button, {
                variant: "tonal",
                onClick: ()=>fetchConfig(selectedGroupId)
            }, t("generic.retry"))
        }));
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PageScroll, null, header, currentDocumentFonts.length === 0 ? /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(FormatIcon, null),
        tone: "neutral",
        text: t("workflows.StyleConverter.noStylesDetected")
    }) : /*#__PURE__*/ React.createElement(MappingList, null, currentDocumentFonts.map((documentFont, index)=>{
        const referenceStyleId = currentToReferenceDocumentMappings[documentFont.styleId] ?? "";
        const previewId = `${previewIdPrefix}-${index}`;
        return /*#__PURE__*/ React.createElement(MappingRow, {
            key: `${documentFont.styleId}__font_stack`
        }, /*#__PURE__*/ React.createElement("div", {
            className: "preview",
            id: previewId
        }, /*#__PURE__*/ React.createElement(Preview, {
            style: previewStyle(documentFont, fontRegistry)
        }, renderWithNumbering(documentFont))), /*#__PURE__*/ React.createElement(MappingSelect, {
            labelId: previewId,
            options: referenceDocumentFonts,
            value: referenceDocumentFonts.find((rf)=>rf.styleId === referenceStyleId),
            onChange: (styleId)=>handleMappingChange(styleId, documentFont.styleId),
            fontRegistry: fontRegistry
        }));
    }))), /*#__PURE__*/ React.createElement(Dock, null, hasSaveError && /*#__PURE__*/ React.createElement(Banner, {
        tone: "error"
    }, t("workflows.StyleConverter.saveMappingFailed")), unmappedCount > 0 && /*#__PURE__*/ React.createElement(ErrorMeta, null, t("workflows.StyleConverter.unmatchedCount", {
        count: unmappedCount
    })), /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        disabled: !areAllMapped,
        onClick: onFormatDocument
    }, t("workflows.StyleConverter.formatDocument"))));
}
